import { useCallback } from 'react';
import { useRelayEnvironment, type GraphQLTaggedNode } from 'react-relay';
import { useApolloClient } from '@apollo/react-hooks';
import { type IEnvironment, type ReaderFragment, type RecordSourceProxy } from 'relay-runtime';

import { fg } from '@confluence/feature-gating';

import { isRelayEnabled } from './isRelayEnabled.ts';
import { getAmbientStoreProxy } from './optimisticInterop/ambientStoreProxy.ts';
import {
	addTypenameToData as addTypenameToDataFromOperations,
	type WithTypename as FragmentWithTypename,
	writeFragmentShim,
} from './fragmentCacheOperations.ts';
import { logRelayMigrationDiagnostic } from './relayDiagnostics.ts';
import { getRelayFragmentIdKind, getRelayFragmentMetadata } from './relayFragment.ts';
import { toDocumentNode } from './toDocumentNode.ts';

export type WithTypename<T> = FragmentWithTypename<T>;

// While a Relay-native optimistic layer is active (inside commitMutation's optimisticUpdater/updater),
// route the fragment write onto that layer's store proxy instead of committing permanently, so it
// participates in the disposable layer Relay reverts atomically. writeFragmentShim only uses
// `environment.commitUpdate`, so a thin env shim runs its in-place write against the provided proxy.
// Outside an updater this returns false and the normal permanent write path is used.
function writeFragmentOntoAmbientLayerIfActive<TData>(
	fragment: GraphQLTaggedNode | undefined,
	fragmentId: string,
	data: TData,
	variables?: Record<string, any>,
): boolean {
	const ambientStore = getAmbientStoreProxy();
	if (!ambientStore || !fragment) {
		return false;
	}
	const ambientEnv = {
		commitUpdate: (updater: (store: RecordSourceProxy) => void) => updater(ambientStore),
	} as unknown as IEnvironment;
	writeFragmentShim(ambientEnv, fragment, fragmentId, data, variables);
	return true;
}

export const addTypenameToData = <TData extends Record<string, any>>(
	data: TData,
	fragmentArtifact: ReaderFragment,
): WithTypename<TData> => addTypenameToDataFromOperations(data, fragmentArtifact);

export type UseWriteFragmentInteropArgs = {
	fragmentSpec: GraphQLTaggedNode;
	/** Fragment artifact for adding __typename to data. __typename will be automatically added. */
	fragmentArtifact: ReaderFragment;
	/** @internal Injected by RelayInteropBabelPlugin — do not set manually */
	__callerPackage__?: string;
};

export type UseWriteFragmentInteropResult<TData> = (params: {
	fragmentId: string;
	data: Omit<WithTypename<TData>, ' $fragmentType'>;
	variables?: Record<string, any>;
	useAggClient?: boolean;
}) => void;

function useWriteFragmentInteropRelay<TData>(
	args: UseWriteFragmentInteropArgs,
): UseWriteFragmentInteropResult<TData> {
	const { fragmentSpec, __callerPackage__ } = args;
	const env = useRelayEnvironment();

	return useCallback(
		({ data, fragmentId, variables }) => {
			// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
			if (fg('confluence_relay_migration_diagnostics_enabled')) {
				const { fragmentName, fragmentType } = getRelayFragmentMetadata(fragmentSpec);
				logRelayMigrationDiagnostic('Fragment access', {
					phase: 'fragment',
					action: 'write',
					api: 'useWriteFragmentInterop',
					callerPackage: __callerPackage__,
					fragmentName,
					fragmentType,
					fragmentIdKind: getRelayFragmentIdKind(fragmentId),
					relayEnabled: true,
				});
			}

			if (writeFragmentOntoAmbientLayerIfActive(fragmentSpec, fragmentId, data, variables)) {
				return;
			}

			writeFragmentShim(env, fragmentSpec, fragmentId, data, variables);
		},
		[env, fragmentSpec, __callerPackage__],
	);
}

function useWriteFragmentInteropApollo<TData>(
	args: UseWriteFragmentInteropArgs,
): UseWriteFragmentInteropResult<TData> {
	const { fragmentSpec, fragmentArtifact, __callerPackage__ } = args;
	const apolloClient = useApolloClient();

	return useCallback(
		({ data, fragmentId, variables, useAggClient }) => {
			// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
			if (fg('confluence_relay_migration_diagnostics_enabled')) {
				const { fragmentName, fragmentType } = getRelayFragmentMetadata(fragmentSpec);
				logRelayMigrationDiagnostic('Fragment access', {
					phase: 'fragment',
					action: 'write',
					api: 'useWriteFragmentInterop',
					callerPackage: __callerPackage__,
					fragmentName,
					fragmentType,
					fragmentIdKind: getRelayFragmentIdKind(fragmentId),
					relayEnabled: false,
				});
			}

			// Add __typename to data using the fragment artifact's type information
			const dataToWrite = addTypenameToData(
				data as Record<string, any>,
				fragmentArtifact,
			) as unknown as TData;

			// Guard window for server usage while preserving Apollo as the original write target.
			const client =
				useAggClient && typeof window !== 'undefined'
					? window.GLOBAL_APOLLO_AGG_CLIENT
					: apolloClient;

			if (client) {
				client.writeFragment<TData>({
					id: fragmentId,
					fragment: toDocumentNode(fragmentSpec),
					data: dataToWrite as TData,
					variables,
				});
			}
		},
		[apolloClient, fragmentSpec, fragmentArtifact, __callerPackage__],
	);
}

export function useWriteFragmentInterop<TData>(
	args: UseWriteFragmentInteropArgs,
): UseWriteFragmentInteropResult<Omit<TData, ' $fragmentType'>> {
	return isRelayEnabled(args.__callerPackage__, { phase: 'fragment' })
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useWriteFragmentInteropRelay(args)
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			useWriteFragmentInteropApollo(args);
}

export type WriteFragmentInteropOptions<TData> = {
	fragmentId: string;
	data: TData;
	fragment?: GraphQLTaggedNode;
	variables?: Record<string, any>;
	useAggClient?: boolean;
	/** @internal Injected by RelayInteropBabelPlugin — do not set manually */
	__callerPackage__?: string;
};

/**
 * Imperative write fragment for use outside of React components (e.g. in mutation `update` callbacks).
 * Mirrors the shape of `useWriteFragmentInterop` but uses global clients directly instead of hooks,
 * analogous to how `readFragmentInterop` is the imperative version of `useReadFragmentInterop`.
 *
 * In the Relay build variant, uses `window.GLOBAL_RELAY_ENV` and `writeFragmentShim`.
 * In the Apollo build variant, uses `window.GLOBAL_APOLLO_CLIENT` (or `window.GLOBAL_APOLLO_AGG_CLIENT`)
 * and `client.writeFragment`.
 *
 * @param options.fragmentId - The Apollo cache key (e.g. `TypeName:id` or `$ROOT_QUERY.fieldName`)
 * @param options.data - The data to write into the cache
 * @param options.fragment - Optional GraphQL fragment. Required for Apollo build variant to correctly
 *   write shaped data. In Relay build variant, the fragment selections are used to traverse fields.
 * @param options.variables - Optional variables for parameterised fragments
 * @param options.useAggClient - If true, uses the AGG Apollo client instead of the default client
 */
export function writeFragmentInterop<TData>(options: WriteFragmentInteropOptions<TData>): void {
	const { fragmentId, data, fragment, variables, useAggClient, __callerPackage__ } = options;

	if (!data) {
		return;
	}

	const source = isRelayEnabled(__callerPackage__, {
		phase: 'fragment',
	})
		? 'relay'
		: 'apollo';
	// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
	if (fg('confluence_relay_migration_diagnostics_enabled')) {
		const { fragmentName, fragmentType } = getRelayFragmentMetadata(fragment);
		logRelayMigrationDiagnostic('Fragment access', {
			phase: 'fragment',
			action: 'write',
			api: 'writeFragmentInterop',
			callerPackage: __callerPackage__,
			fragmentName,
			fragmentType,
			fragmentIdKind: getRelayFragmentIdKind(fragmentId),
			relayEnabled: source === 'relay',
		});
	}

	if (source === 'relay') {
		const env = window['GLOBAL_RELAY_ENV']!;
		if (fragment) {
			if (writeFragmentOntoAmbientLayerIfActive(fragment, fragmentId, data, variables)) {
				return;
			}

			writeFragmentShim(env, fragment, fragmentId, data, variables);
		}
		return;
	}

	const client =
		useAggClient && typeof window !== 'undefined'
			? window['GLOBAL_APOLLO_AGG_CLIENT']
			: window['GLOBAL_APOLLO_CLIENT'];

	if (client && fragment) {
		const dataToWrite = addTypenameToDataFromOperations(
			data as Record<string, any>,
			fragment as ReaderFragment,
		) as unknown as TData;

		client.writeFragment<TData>({
			id: fragmentId,
			fragment: toDocumentNode(fragment),
			data: dataToWrite,
			variables,
		});
	}
}
